import React from 'react';
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

// Enhanced button variants with consistent styling
const buttonVariants = {
    variant: {
        default: "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 shadow-sm hover:shadow-md",
        destructive: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500 shadow-sm hover:shadow-md transition-all duration-200",
        outline: "border border-blue-300 bg-white hover:bg-blue-50 hover:border-blue-400 text-blue-900 focus-visible:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow-md",
        secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 focus-visible:ring-gray-500 shadow-sm hover:shadow-md transition-all duration-200",
        ghost: "hover:bg-blue-50 text-blue-900 focus-visible:ring-blue-500 transition-all duration-200",
        link: "text-blue-600 underline-offset-4 hover:underline focus-visible:ring-blue-500 transition-colors duration-200",
        success: "bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500 shadow-sm hover:shadow-md transition-all duration-200",
        warning: "bg-yellow-600 text-white hover:bg-yellow-700 focus-visible:ring-yellow-500 shadow-sm hover:shadow-md transition-all duration-200",
        danger: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500 shadow-sm hover:shadow-md transition-all duration-200",
        // Enhanced colorful variants with consistent design
        livechat: "bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-md transition-all duration-200 hover:shadow-lg focus-visible:ring-green-500",
        callsupport: "bg-gradient-to-r from-blue-500 to-cyan-600 text-white hover:from-blue-600 hover:to-cyan-700 shadow-md transition-all duration-200 hover:shadow-lg focus-visible:ring-blue-500",
        purple: "bg-gradient-to-r from-purple-500 to-violet-600 text-white hover:from-purple-600 hover:to-violet-700 shadow-md transition-all duration-200 hover:shadow-lg focus-visible:ring-purple-500",
        orange: "bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:from-orange-600 hover:to-amber-700 shadow-md transition-all duration-200 hover:shadow-lg focus-visible:ring-orange-500",
        teal: "bg-gradient-to-r from-teal-500 to-cyan-600 text-white hover:from-teal-600 hover:to-cyan-700 shadow-md transition-all duration-200 hover:shadow-lg focus-visible:ring-teal-500",
        pink: "bg-gradient-to-r from-pink-500 to-rose-600 text-white hover:from-pink-600 hover:to-rose-700 shadow-md transition-all duration-200 hover:shadow-lg focus-visible:ring-pink-500",
        // Primary blue variant for consistent branding
        primary: "bg-blue-600 text-white hover:bg-blue-700 shadow-md transition-all duration-200 hover:shadow-lg focus-visible:ring-blue-500",
        // Blue outline variant for consistency with design
        blue: "border border-blue-600 bg-transparent text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-200 focus-visible:ring-blue-500 shadow-sm hover:shadow-md"
    },
    size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-sm",
        lg: "h-11 rounded-md px-8 text-base",
        icon: "h-10 w-10",
        xs: "h-8 rounded-md px-2 text-xs",
        xl: "h-12 rounded-md px-10 text-lg",
    },
};

const Button = React.forwardRef(({
    className = "",
    variant = "default",
    size = "default",
    asChild = false,
    children,
    loading = false,
    iconName = null,
    iconPosition = 'left',
    iconSize = null,
    fullWidth = false,
    disabled = false,
    ...props
}, ref) => {
    // Icon size mapping based on button size
    const iconSizeMap = {
        xs: 12,
        sm: 14,
        default: 16,
        lg: 18,
        xl: 20,
        icon: 16,
    };

    const calculatedIconSize = iconSize || iconSizeMap?.[size] || 16;

    // Enhanced loading spinner with better animation
    const LoadingSpinner = () => (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    const renderIcon = () => {
        if (!iconName) return null;
        try {
            return (
                <Icon
                    name={iconName}
                    size={calculatedIconSize}
                    className={cn(
                        "flex-shrink-0",
                        children && iconPosition === 'left' && "mr-2",
                        children && iconPosition === 'right' && "ml-2"
                    )}
                />
            );
        } catch (error) {
            console.warn(`Icon "${iconName}" not found:`, error);
            return null;
        }
    };

    // Get variant and size classes with fallback
    const variantClass = buttonVariants?.variant?.[variant] || buttonVariants?.variant?.default;
    const sizeClass = buttonVariants?.size?.[size] || buttonVariants?.size?.default;
    
    const buttonClasses = cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        variantClass,
        sizeClass,
        fullWidth && "w-full",
        "hover:scale-[1.02] active:scale-[0.98]", // Subtle scale effect
        className
    );

    return (
        <button
            className={buttonClasses}
            ref={ref}
            disabled={disabled || loading}
            {...props}
        >
            {loading && <LoadingSpinner />}
            {!loading && iconName && iconPosition === 'left' && renderIcon()}
            {children && <span className="truncate">{children}</span>}
            {!loading && iconName && iconPosition === 'right' && renderIcon()}
        </button>
    );
});

Button.displayName = "Button";
export default Button;